/** The two `normalizeUrl` projections over a property's image list: the deduped image RECORDS and the bare URL strings.
 *
 * Relocated from `tools/property/{normalize-property-images,extract-property-image-urls}.ts` 2026-08-20 under the
 * tools-are-flow-only directive. No flow imported either half and every consumer is a rendering-tier surface (gallery
 * hooks, the explore feed, the property card, the SEO structured-data builder), so `components/lib/` -- the sanctioned
 * non-presentational subtree (`QUAL-COMP-PURE-001` excludeGlob) -- is the home; a `utils/` leaf could not have taken
 * them, since the body value-imports a utils sibling the import floor forbids.
 *
 * They were two leaves applying the SAME normalization to the same input for two result shapes, so they are one leaf
 * now: a normalization change cannot land on one projection and miss the other. */

import { normalizeUrl } from '@federa/utils/strings/url';
import type { PropertyImage } from '@federa/types/properties-image';

/** Deduped image records, url-normalized; an image whose url does not normalize is dropped. */
export function normalizePropertyImages(images?: PropertyImage[]): PropertyImage[] {
	if (!images || images.length === 0) return [] as PropertyImage[];
	const seen = new Set<string>();
	const result: PropertyImage[] = [];
	for (const image of images) {
		if (!image.url) continue;
		const normalized = normalizeUrl(image.url);
		if (!normalized) continue;
		const key = normalized.toLowerCase();
		if (seen.has(key)) continue;
		seen.add(key);
		result.push({ ...image, url: normalized });
	}
	return result;
}

/** The normalized url STRINGS of an image list, in order; an image whose url does not normalize contributes nothing. */
export function extractPropertyImageUrls(imagesList: PropertyImage[]): string[] {
	const result: string[] = [];
	for (const img of imagesList) {
		const normalized = normalizeUrl(img.url);
		if (normalized) result.push(normalized);
	}
	return result;
}
